import { defineStore } from 'pinia';

import { useNotify } from '@/app/providers/notify';
import { additionalApiConfig } from '@/config/api/additionals';
import { useClientApi } from '@/plugins/api/lk';
import { useUserRole } from '@/shared/composables';
import { openLinkInNewTab } from '@/shared/lib/utils/openLinkInNewTab';
import { getErrorMessageTyped } from '@/shared/services';
import type {
  Agreement,
  Document,
} from '@/types/Agreements';

interface State {
  currentAdditionalAgreement: Agreement | object;
}

async function notifyError (error: unknown, fallbackMessage?: string): Promise<void> {
  const notifier = useNotify();

  if (!notifier) {
    return;
  }

  const message = getErrorMessageTyped(error, fallbackMessage);

  notifier.create({
    message,
    type: 'negative',
  });

  throw new Error(message);
}

export const useAdditionalAgreementStore = defineStore('additionalAgreement', {
  state: (): State => ({
    currentAdditionalAgreement: {},
  }),

  getters: {},

  actions: {
    async getAdditionalAgreement (id: number): Promise<void> {
      try {
        const { role } = useUserRole();
        const url = additionalApiConfig.additionalAgreementById(role, id);

        const {
          data,
        } = await useClientApi().get<Agreement>(url);

        this.currentAdditionalAgreement = data;
      } catch (error: unknown) {
        console.log('🚀 ~ file: additionalAgreement.ts ~ getAdditionalAgreement ~ error', error);
        this.$sentry.captureException(error);
      }
    },

    async getAdditionalAgreementDocument (id: number): Promise<void> {
      const fallbackMessage = 'Не удалось получить документ';

      try {
        const { role } = useUserRole();
        const url = additionalApiConfig.getDocument(role, id);

        const {
          data,
        } = await useClientApi().get<Document>(url);

        const {
          aws: link,
        } = data.files?.[0]?.files?.[0] ?? {};

        if (!link) {
          throw new Error(fallbackMessage);
        }

        openLinkInNewTab(link);
      } catch (error: unknown) {
        console.log('🚀 ~ file: additionalAgreement.ts ~ getAdditionalAgreementDocument ~ error', error);
        this.$sentry.captureException(error);

        await notifyError(error, fallbackMessage);
      }
    },
  },
});
